import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner, Row, Col } from 'react-bootstrap';
import InvoiceRuleService from '../services/invoiceRuleService';
import invoiceService from '../services/invoiceService';
import { useI18n } from '../i18n/I18nContext';

// Best Practice: Country normalization map synced with backend
const COUNTRY_MAP = {
    'FRANCE': 'FR',
    'MAROC': 'MA', 'MOROCCO': 'MA',
    'BELGIQUE': 'BE', 'BELGIUM': 'BE',
    'ESPAGNE': 'ES', 'SPAIN': 'ES',
    'ITALIE': 'IT', 'ITALY': 'IT',
    'ALLEMAGNE': 'DE', 'GERMANY': 'DE',
    'PAYS-BAS': 'NL', 'NETHERLANDS': 'NL',
    'SUISSE': 'CH', 'SWITZERLAND': 'CH',
    'LUXEMBOURG': 'LU'
};

const normalizeCountryCode = (country) => {
    if (!country) return 'MA';
    const normalized = country.toString().trim().toUpperCase();
    return COUNTRY_MAP[normalized] || normalized;
};

const InvoiceGenerationPopup = ({ show, handleClose, client, transporteur, totals, onConfirm, onPreview }) => {
    const { t } = useI18n();
    const [loading, setLoading] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [rules, setRules] = useState(null);
    const [formData, setFormData] = useState({
        seller: {},
        buyer: {},
        invoice: {}
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (show) {
            setLoading(true);
            const sellerCountry = normalizeCountryCode(transporteur?.country);
            const buyerCountry = normalizeCountryCode(client?.country);
            
            Promise.all([
                InvoiceRuleService.getRules(sellerCountry),
                InvoiceRuleService.getRules(buyerCountry),
                invoiceService.getNextInvoiceNumber()
            ])
            .then(([sellerData, buyerData, nextNumber]) => {
                let sReq = sellerData ? sellerData.seller_required : [];
                if (!sReq.includes('bic')) sReq.push('bic');
                const combinedRules = {
                    ...(sellerData || {}),
                    seller_required: sReq,
                    buyer_required: buyerData ? buyerData.buyer_required : [],
                    invoice_required: sellerData ? sellerData.invoice_required : []
                };
                setRules(combinedRules);
                initializeForm(combinedRules, nextNumber);
            })
            .catch(err => {
                console.error("Error loading rules:", err);
            })
            .finally(() => setLoading(false));
        }
    }, [show, client, transporteur]);

    const initializeForm = (rules, nextNumber) => {
        if (!rules) return;

        const formatAddress = (obj) => {
            if (!obj) return '';
            
            const baseAddress = obj.address || '';
            const city = obj.city || '';
            const zip = obj.zipCode || obj.postalCode || '';
            const country = obj.country || '';

            // Split existing address to get unique parts
            const parts = baseAddress.split(',').map(p => p.trim()).filter(Boolean);
            const uniqueParts = new Set(parts);

            // Add city, zip, country if not already clearly in the parts
            if (city && ![...uniqueParts].some(p => p.toLowerCase().includes(city.toLowerCase()))) {
                uniqueParts.add(city);
            }
            if (zip && ![...uniqueParts].some(p => p.includes(zip))) {
                uniqueParts.add(zip);
            }
            if (country && ![...uniqueParts].some(p => p.toLowerCase().includes(country.toLowerCase()))) {
                uniqueParts.add(country);
            }

            return Array.from(uniqueParts).join(', ');
        };

        // Cross-border detection - Best Practice: Normalized comparison
        const sCountry = normalizeCountryCode(transporteur?.country);
        const bCountry = normalizeCountryCode(client?.country);
        const isCrossBorder = sCountry !== bCountry;

        // Map existing data to form
        const initialData = {
            seller: {},
            buyer: {},
            invoice: {
                invoice_number: nextNumber || 'AUTO',
                issue_date: new Date().toISOString().split('T')[0],
                due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Default 30 days
                vat_rate: isCrossBorder ? 0 : (rules.standard_vat_rate || rules.default_vat_rate || 20),
                legalNotice: isCrossBorder ? (rules.international_exemption_mention || '') : ''
            }
        };

        // Pre-fill Seller (Transporteur)
        if (rules.seller_required) {
            rules.seller_required.forEach(field => {
                let value = '';
                if (!transporteur) value = '';
                else if (field === 'legal_name') {
                    // SaaS Best Practice: Strictly prioritize the official Enterprise Name over personal names
                    value = transporteur.enterpriseName || transporteur.enterprise_name || transporteur.entreprise || '';
                    if (!value && (transporteur.firstname || transporteur.lastname)) {
                        value = `${transporteur.firstname || ''} ${transporteur.lastname || ''}`.trim();
                    }
                }
                else if (field === 'address') value = formatAddress(transporteur);
                else if (field === 'vat_number') value = transporteur.vatNumber || transporteur.vat_number || '';
                else if (field === 'ice') value = transporteur.ice || '';
                else if (field === 'iban') value = transporteur.iban || '';
                else if (field === 'bic') value = transporteur.bic || '';
                else if (field === 'bank_name') value = transporteur.bankName || transporteur.bank_name || '';
                else if (field === 'siret') value = transporteur.siret || '';
                else if (field === 'siren') value = transporteur.siren || '';
                else if (field === 'email') value = transporteur.email || '';
                else if (field === 'phone') value = transporteur.telephone || transporteur.phone || '';
                else if (field === 'if' || field === 'identifiant_fiscal') value = transporteur.identifiantFiscal || transporteur.if || '';
                else if (field === 'siren') value = transporteur.siren || '';
                else if (field === 'siret') value = transporteur.siret || '';
                else if (field === 'company_number') value = transporteur.companyNumber || '';
                else if (field === 'nif') value = transporteur.nif || '';
                else if (field === 'codice_fiscale') value = transporteur.codiceFiscale || '';
                else if (field === 'uid') value = transporteur.uid || '';
                else if (field === 'steuer_number') value = transporteur.steuerNumber || '';
                else if (field === 'rcs_number') value = transporteur.rcsNumber || '';
                else if (field === 'tp') value = transporteur.tp || '';
                else if (field === 'kvk_number') value = transporteur.kvkNumber || '';

                initialData.seller[field] = value || '';
            });
        }

        // Pre-fill Buyer (Client)
        if (rules.buyer_required) {
            rules.buyer_required.forEach(field => {
                let value = '';
                if (!client) value = '';
                else if (field === 'legal_name') value = client.entreprise || client.enterpriseName || client.enterprise_name || `${client.prenom || ''} ${client.nom || ''}`.trim();
                else if (field === 'address') value = formatAddress(client);
                else if (field === 'vat_number') value = client.vatNumber || client.vat_number || '';
                else if (field === 'ice') value = client.ice || '';
                else if (field === 'if' || field === 'identifiant_fiscal') value = client.identifiantFiscal || client.if || '';
                else if (field === 'siren') value = client.siren || '';
                else if (field === 'siret') value = client.siret || '';
                else if (field === 'company_number') value = client.companyNumber || '';
                else if (field === 'nif') value = client.nif || '';
                else if (field === 'codice_fiscale') value = client.codiceFiscale || '';
                else if (field === 'uid') value = client.uid || '';
                else if (field === 'steuer_number') value = client.steuerNumber || '';
                else if (field === 'rcs_number') value = client.rcsNumber || '';
                else if (field === 'kvk_number') value = client.kvkNumber || '';
                else if (field === 'phone') value = client.telephone || client.phone || '';

                initialData.buyer[field] = value || '';
            });
        }

        // SaaS Best Practice: Pre-calculate initial totals from missions
        if (client && transporteur && Array.isArray(client.missions)) {
            // This assumes missions are passed or available. 
            // For now, we will let the user or the first calculation handle it.
        }
        
        // Initial HT/VAT/TTC pre-fill (Best Practice: start with calculated values from prop)
        const ht = totals?.ht || 0;
        const rate = initialData.invoice.vat_rate || 0;
        const calculatedVat = (ht * rate / 100).toFixed(2);
        const calculatedTtc = (ht + parseFloat(calculatedVat)).toFixed(2);

        initialData.invoice.total_ht = ht.toFixed(2);
        initialData.invoice.total_vat = calculatedVat;
        initialData.invoice.total_ttc = calculatedTtc;
        
        if (transporteur && transporteur.defaultInvoiceLegalMentions) {
            initialData.invoice.legalNotice = transporteur.defaultInvoiceLegalMentions;
        }

        setFormData(initialData);
    };

    const handleChange = (section, field, value) => {
        setFormData(prev => {
            const newData = { ...prev };
            newData[section] = { ...prev[section], [field]: value };

            // Best Practice: Auto-calculate VAT and TTC if HT or Rate changes
            if (section === 'invoice' && (field === 'total_ht' || field === 'vat_rate')) {
                const ht = parseFloat(newData.invoice.total_ht || 0);
                const rate = parseFloat(newData.invoice.vat_rate || 0);
                if (!isNaN(ht) && !isNaN(rate)) {
                    const vat = (ht * rate / 100).toFixed(2);
                    const ttc = (ht + parseFloat(vat)).toFixed(2);
                    newData.invoice.total_vat = vat;
                    newData.invoice.total_ttc = ttc;
                }
            } else if (section === 'invoice' && field === 'total_vat') {
                // Adjust TTC if VAT is manually changed
                const ht = parseFloat(newData.invoice.total_ht || 0);
                const vat = parseFloat(value || 0);
                if (!isNaN(ht) && !isNaN(vat)) {
                    newData.invoice.total_ttc = (ht + vat).toFixed(2);
                }
            }

            return newData;
        });

        // Clear error
        if (errors[`${section}.${field}`]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[`${section}.${field}`];
                return newErrors;
            });
        }
    };

    const validate = () => {
        if (!rules) return true;

        const newErrors = {};
        
        // Best Practice: Require legal notice for 0% VAT
        const rate = parseFloat(formData.invoice.vat_rate);
        if (!isNaN(rate) && rate === 0) {
            if (!formData.invoice.legalNotice || formData.invoice.legalNotice.trim() === '') {
                newErrors['invoice.legalNotice'] = t('invoice.errors.exemptionRequired', 'Mention d\'exonération obligatoire pour un taux à 0%');
                alert(t('invoice.errors.exemptionRequired', 'Une mention légale d\'exonération est obligatoire pour un taux de TVA à 0% (ex: Exonéré Art. 259 CGI).'));
                return false;
            }
        }

        setErrors(newErrors);
        return true;
    };

    const handleConfirm = () => {
        if (validate()) {
            const payload = {
                sellerLegalInfo: JSON.stringify(formData.seller),
                buyerLegalInfo: JSON.stringify(formData.buyer),
                invoiceLegalMentions: formData.invoice.legalNotice || '',
                missingNotes: formData.invoice.missingNotes || '',
                invoiceNumber: formData.invoice.invoice_number,
                issueDate: formData.invoice.issue_date,
                dueDate: formData.invoice.due_date,
                vatRate: formData.invoice.vat_rate,
                totalHt: formData.invoice.total_ht,
                totalVat: formData.invoice.total_vat,
                totalTtc: formData.invoice.total_ttc,
                sellerIdentifiantFiscal: formData.seller.if || formData.seller.identifiant_fiscal,
                buyerIdentifiantFiscal: formData.buyer.if || formData.buyer.identifiant_fiscal,
            };
            onConfirm(payload);
        }
    };

    const handlePreview = async () => {
        if (validate()) {
            setPreviewLoading(true);
            const payload = {
                sellerLegalInfo: JSON.stringify(formData.seller),
                buyerLegalInfo: JSON.stringify(formData.buyer),
                invoiceLegalMentions: formData.invoice.legalNotice || '',
                missingNotes: formData.invoice.missingNotes || '',
                invoiceNumber: formData.invoice.invoice_number,
                issueDate: formData.invoice.issue_date,
                dueDate: formData.invoice.due_date,
                vatRate: formData.invoice.vat_rate,
                totalHt: formData.invoice.total_ht,
                totalVat: formData.invoice.total_vat,
                totalTtc: formData.invoice.total_ttc,
                sellerIdentifiantFiscal: formData.seller.if || formData.seller.identifiant_fiscal,
                buyerIdentifiantFiscal: formData.buyer.if || formData.buyer.identifiant_fiscal,
            };
            try {
                await onPreview(payload);
            } finally {
                setPreviewLoading(false);
            }
        }
    };

    const renderField = (section, fieldName, isRequired) => {
        const labelKey = `invoice.fields.${fieldName}`; // Translation key
        // Fallback label logic if translation missing could be fieldName capitalized

        return (
            <Form.Group className="mb-3" key={fieldName}>
                <Form.Label>
                    {t(labelKey, { defaultValue: fieldName })}
                    {isRequired && <span className="text-danger"> *</span>}
                </Form.Label>
                <Form.Control
                    type="text"
                    placeholder={t(labelKey).includes('.') ? fieldName : t(labelKey)}
                    value={(formData[section][fieldName] !== undefined && formData[section][fieldName] !== null) ? formData[section][fieldName] : ''}
                    onChange={(e) => handleChange(section, fieldName, e.target.value)}
                    isInvalid={!!errors[`${section}.${fieldName}`]}
                />
                <Form.Control.Feedback type="invalid">
                    {errors[`${section}.${fieldName}`]}
                </Form.Control.Feedback>
            </Form.Group>
        );
    };

    if (!show) return null;

    return (
        <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>{t('invoice.generationTitle', 'Generate Invoice - Compliance Check')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-2">{t('common.loading')}</p>
                    </div>
                ) : !rules ? (
                    <Alert variant="warning">
                        {t('invoice.noRulesFound', 'No specific tax rules found for this country. Standard generation will apply.')}
                    </Alert>
                ) : (
                    <Form>
                        <Row>
                            <Col md={6}>
                                <h5 className="mb-3 text-primary">{t('invoice.sellerSection', 'Seller Information')}</h5>
                                {rules.seller_required && rules.seller_required.map(field =>
                                    renderField('seller', field, true)
                                )}
                            </Col>
                            <Col md={6}>
                                <h5 className="mb-3 text-primary">{t('invoice.buyerSection', 'Buyer Information')}</h5>
                                {rules.buyer_required && rules.buyer_required.map(field =>
                                    renderField('buyer', field, true)
                                )}
                            </Col>
                        </Row>
                        <hr />
                        <h5 className="mb-3 text-primary">{t('invoice.detailsSection', 'Invoice Details')}</h5>
                        <Row>
                            <Col md={4}>
                                {renderField('invoice', 'invoice_number', true)}
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('invoice.fields.issue_date')}</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={formData.invoice.issue_date || ''}
                                        onChange={(e) => handleChange('invoice', 'issue_date', e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('invoice.fields.due_date')}</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={formData.invoice.due_date || ''}
                                        onChange={(e) => handleChange('invoice', 'due_date', e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <hr />
                        {/* Summary Totals Row - Best Practice: Side by Side HT and TTC */}
                        <Row className="bg-light p-3 rounded-3 mb-3 mx-0 border shadow-sm align-items-center">
                            <Col md={6}>
                                <div className="d-flex align-items-center">
                                    <div className="text-muted fw-bold text-uppercase small me-2">{t('invoice.fields.total_ht')}:</div>
                                    <div className="fs-4 fw-bold text-dark">{formData.invoice.total_ht || '0.00'}</div>
                                </div>
                            </Col>
                            <Col md={6} className="border-start">
                                <div className="d-flex align-items-center">
                                    <div className="ms-md-3 text-primary fw-bold text-uppercase small me-2">{t('invoice.fields.total_ttc')}:</div>
                                    <div className="fs-3 fw-bold text-primary">{formData.invoice.total_ttc || '0.00'}</div>
                                </div>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                {renderField('invoice', 'vat_rate', true)}
                            </Col>
                            <Col md={6}>
                                {renderField('invoice', 'total_vat', true)}
                            </Col>
                        </Row>
                        <hr />
                        <Row>
                            <Col>
                                <h5 className="mb-3 text-primary">{t('invoice.footerSection')}</h5>
                                <p className="text-muted small mb-3">
                                    {t('invoice.footerSectionHint')}
                                </p>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('invoice.footerLabel')}</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        placeholder={t('invoice.legalNoticePlaceholder', "Ex: Membre d'une association agréée...")}
                                        value={formData.invoice.legalNotice || ''}
                                        onChange={(e) => handleChange('invoice', 'legalNotice', e.target.value)}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Notes PMT, PMD, AAB manquantes</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        placeholder="Ex: PMT Manquantes..."
                                        value={formData.invoice.missingNotes || ''}
                                        onChange={(e) => handleChange('invoice', 'missingNotes', e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    {t('common.cancel')}
                </Button>
                <Button
                    variant="outline-primary"
                    onClick={handlePreview}
                    disabled={loading || previewLoading}
                >
                    {previewLoading ? <Spinner animation="border" size="sm" className="me-2" /> : null}
                    {t('common.preview')}
                </Button>
                <Button variant="primary" onClick={handleConfirm} disabled={loading || previewLoading}>
                    {loading ? <Spinner animation="border" size="sm" className="me-2" /> : null}
                    {t('common.confirmAndGenerate')}
                </Button>
            </Modal.Footer>
        </Modal >
    );
};

export default InvoiceGenerationPopup;
